import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import Dashboard from './components/Dashboard';
import Properties from './components/Properties';
import Tenants from './components/Tenants';
import Maintenance from './components/Maintenance';
import Login from './components/Login';
import Register from './components/Register';
import LandingPage from './components/LandingPage';
import PreLaunchPage from './components/PreLaunchPage';
import FeaturesPage from './components/FeaturesPage';
import AboutPage from './components/AboutPage';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import PilotAccess from './components/PilotAccess';
import InterviewPage from './components/research/InterviewPage';
import MaintenanceApproval from './components/MaintenanceApproval';
import PropertyDetail from './components/PropertyDetail';
import NivaChat from './components/NivaChat';
import ApplyChat from './components/ApplyChat';
import ApplyForm from './components/ApplyForm';
import Applications from './components/Applications';
import LandlordAssessment from './components/LandlordAssessment';
import RomiScorePage from './components/RomiScorePage';
import Messages from './components/Messages';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import ComingSoon from './components/ComingSoon';
import Payments from './components/Payments';
import RentCollection from './components/RentCollection';
import UnsubscribePage from './components/UnsubscribePage';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import Providers from './components/Providers';
import Settings from './components/Settings';
import AdminLeads from './components/admin/AdminLeads';
import AdminFlags from './components/admin/AdminFlags';
import AdminOrgs from './components/admin/AdminOrgs';
import { authService } from './services/authService';
import { waitForAuthReady } from './services/firebase';
import featureFlagService from './services/featureFlagService';
import useIdleTimeout, { WARN_BEFORE_MS } from './hooks/useIdleTimeout';
import usePageTracking from './hooks/usePageTracking';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({
    REGISTRATION_ENABLED: false,
    LOGIN_ENABLED: false,
    ADMIN_ENABLED: false,
  });
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [countdown, setCountdown] = useState(Math.floor(WARN_BEFORE_MS / 1000));
  const countdownRef = useRef(null);

  useEffect(() => {
    Promise.all([checkAuthStatus(), checkFeatureFlags()])
      .finally(() => setLoading(false));
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Legacy JWT session
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        return;
      }
      // Firebase social session — wait for auth state to be restored from persistence
      const firebaseUser = await waitForAuthReady();
      if (firebaseUser) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      }
    } catch (error) {
      console.log('No valid session found');
      localStorage.removeItem('token');
    }
  };

  const checkFeatureFlags = async () => {
    try {
      const flags = await featureFlagService.getEnabledFeatures();
      setFeatureFlags(flags);
    } catch (error) {
      console.error('Error loading feature flags:', error);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setShowIdleWarning(false);
    clearInterval(countdownRef.current);
  }, []);

  const handleIdleWarn = useCallback(() => {
    setShowIdleWarning(true);
    setCountdown(Math.floor(WARN_BEFORE_MS / 1000));
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  usePageTracking();

  const { resetTimer } = useIdleTimeout({
    onWarn: handleIdleWarn,
    onIdle: handleLogout,
    enabled: !!user
  });

  const handleStayLoggedIn = () => {
    setShowIdleWarning(false);
    clearInterval(countdownRef.current);
    setCountdown(Math.floor(WARN_BEFORE_MS / 1000));
    resetTimer();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
    {showIdleWarning && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h2 className="text-lg font-semibold mb-2">Session expiring soon</h2>
          <p className="text-sm text-gray-600 mb-4">
            You've been inactive for a while. You'll be logged out in{' '}
            <span className="font-semibold text-red-600">{countdown}s</span>.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Log out
            </button>
            <button
              onClick={handleStayLoggedIn}
              className="px-4 py-2 text-sm rounded-md bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
            >
              Stay logged in
            </button>
          </div>
        </div>
      </div>
    )}
    <Routes>
      {/* Public Landing Page */}
      <Route
        path="/"
        element={<LandingPage />}
      />

      {/* Features Page */}
      <Route
        path="/features"
        element={<FeaturesPage />}
      />

      {/* About Page */}
      <Route
        path="/about"
        element={<AboutPage />}
      />

      {/* Blog Routes */}
      <Route
        path="/blog"
        element={<BlogList />}
      />
      <Route
        path="/blog/:slug"
        element={<BlogPost />}
      />

      {/* ROMI Score — SEO landing page */}
      <Route
        path="/romi-score"
        element={<RomiScorePage />}
      />

      {/* ROMI Score — interactive AI assessment tool */}
      <Route
        path="/niva-romi-score"
        element={<LandlordAssessment />}
      />

      {/* Research Interview Route (Public) */}
      <Route
        path="/interview/:surveyId"
        element={<InterviewPage />}
      />

      {/* Unsubscribe Route (Public) */}
      <Route
        path="/unsubscribe"
        element={<UnsubscribePage />}
      />

      {/* Terms and Conditions (Public) */}
      <Route
        path="/terms"
        element={<TermsAndConditions />}
      />

      {/* Privacy Policy (Public) */}
      <Route
        path="/privacy"
        element={<PrivacyPolicy />}
      />

      {/* Apply — redirect to register so invite codes are handled through the main UX */}
      <Route
        path="/apply"
        element={
          user && user.userType === 'Applicant'
            ? <Navigate to="/apply/form" />
            : <Navigate to="/register" />
        }
      />

      {/* Apply Form — guided multi-step application form */}
      <Route
        path="/apply/form"
        element={
          user ? (
            user.userType === 'Applicant'
              ? <ApplyForm user={user} onLogout={handleLogout} onLogin={handleLogin} />
              : <Navigate to="/niva" />
          ) : (
            <Navigate to="/apply" />
          )
        }
      />

      {/* Apply Chat — legacy AI chat flow (kept but no longer the default) */}
      <Route
        path="/apply/chat"
        element={
          user ? (
            user.userType === 'Applicant'
              ? <ApplyChat user={user} onLogout={handleLogout} />
              : <Navigate to="/niva" />
          ) : (
            <Navigate to="/apply" />
          )
        }
      />

      {/* Login Route - Show PreLaunchPage if login disabled */}
      <Route
        path="/login"
        element={
          !user ? (
            featureFlags.LOGIN_ENABLED ? (
              <Login onLogin={handleLogin} />
            ) : (
              <PreLaunchPage />
            )
          ) : (
            <Navigate to="/niva" />
          )
        }
      />

      {/* Register Route - Show PreLaunchPage if registration disabled */}
      <Route
        path="/register"
        element={
          !user ? (
            featureFlags.REGISTRATION_ENABLED ? (
              <Register onRegister={handleLogin} />
            ) : (
              <PreLaunchPage />
            )
          ) : (
            <Navigate to="/niva" />
          )
        }
      />
      
      {/* Pilot Access - Magic Link Entry Point */}
      <Route
        path="/access"
        element={<PilotAccess />}
      />

      {/* Password Reset Routes */}
      <Route
        path="/forgot-password"
        element={<ForgotPassword />}
      />
      <Route
        path="/reset-password/:token"
        element={<ResetPassword />}
      />

      {/* Email Verification Routes */}
      <Route
        path="/verify-email"
        element={<VerifyEmail />}
      />
      <Route
        path="/verify-email/:token"
        element={<VerifyEmail />}
      />
      
      {/* Post-registration onboarding */}
      <Route
        path="/welcome"
        element={
          user ? (
            <WelcomeOnboarding user={user} />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          user ? (
            user.userType === 'Applicant'
              ? <Navigate to="/apply/form" />
              : (
                <MainLayout user={user} onLogout={handleLogout}>
                  <Dashboard user={user} />
                </MainLayout>
              )
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* Landlord — Applications view */}
      <Route
        path="/applications"
        element={
          user ? (
            user.userType === 'Landlord'
              ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <Applications user={user} />
                </MainLayout>
              )
              : <Navigate to="/dashboard" />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/properties"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <Properties />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/properties/:id"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <PropertyDetail user={user} />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route 
        path="/tenants" 
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <Tenants />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } 
      />
      <Route
        path="/providers"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <Providers />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/maintenance/approval/:caseId"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <MaintenanceApproval user={user} />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/maintenance"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <Maintenance user={user} />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/niva"
        element={
          user ? (
            user.userType === 'Applicant'
              ? <Navigate to="/apply/form" />
              : (
                <MainLayout user={user} onLogout={handleLogout}>
                  <NivaChat user={user} />
                </MainLayout>
              )
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/messages"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <Messages />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/team"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <ComingSoon path="/team" />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/reports"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <ComingSoon path="/reports" />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/settings"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <Settings user={user} />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/help"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <ComingSoon path="/help" />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* Admin Routes — guarded by ADMIN_ENABLED feature flag */}
      <Route
        path="/admin"
        element={
          user ? (
            featureFlags.ADMIN_ENABLED ? (
              <Navigate to="/admin/leads" />
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/admin/leads"
        element={
          user ? (
            featureFlags.ADMIN_ENABLED ? (
              <MainLayout user={user} onLogout={handleLogout}>
                <AdminLeads />
              </MainLayout>
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/admin/flags"
        element={
          user ? (
            featureFlags.ADMIN_ENABLED ? (
              <MainLayout user={user} onLogout={handleLogout}>
                <AdminFlags />
              </MainLayout>
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/admin/orgs"
        element={
          user ? (
            featureFlags.ADMIN_ENABLED ? (
              <MainLayout user={user} onLogout={handleLogout}>
                <AdminOrgs />
              </MainLayout>
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      <Route
        path="/lease"
        element={
          user ? (
            <MainLayout user={user} onLogout={handleLogout}>
              <ComingSoon path="/lease" />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/payments"
        element={
          user ? (
            user.userType === 'Tenant'
              ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <Payments user={user} />
                </MainLayout>
              )
              : <Navigate to="/dashboard" />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/niva-pay"
        element={
          user ? (
            user.userType === 'Landlord'
              ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <RentCollection />
                </MainLayout>
              )
              : <Navigate to="/dashboard" />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
    </Routes>
    </>
  );
}

export default App;